<template>
  <div
    class="flex flex-col justify-center items-center fixed z-50 inset-0 bg-opacity-25 bg-black"
    @click.self="close"
  >
    <div class="form-card sm:w-2/3 lg:w-1/2 relative max-h-[90vh] overflow-y-auto">
      <button class="absolute top-4 right-4 system-button" @click.prevent="close">
        <close-icon class="w-6 fill-current" />
      </button>

      <!-- Header -->
      <div class="bg-gradient-to-r from-primary-50 to-secondary-50 -m-6 mb-6 p-6 rounded-t-lg">
        <h2 class="text-2xl font-bold text-gray-800">Detalles de la Dieta</h2>
        <p v-if="diet.pet" class="text-gray-600 mt-1">{{ diet.pet.name }}</p>
      </div>

      <!-- Main Info Cards -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <!-- Diet Info Card -->
        <div class="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <h3 class="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Información de la Dieta</h3>

          <div class="space-y-3">
            <div>
              <p class="text-xs text-gray-500">Nombre</p>
              <p class="font-medium text-gray-900">{{ diet.name }}</p>
            </div>

            <div>
              <p class="text-xs text-gray-500">Precio Semanal</p>
              <p class="font-semibold text-lg text-primary-600">{{ integerToCurrency(diet.weekly_price) }}</p>
            </div>

            <div v-if="diet.diet_stage">
              <p class="text-xs text-gray-500">Etapa</p>
              <p class="font-medium text-gray-900">{{ diet.diet_stage.display_name }}</p>
            </div>

            <div v-if="diet.diet_type">
              <p class="text-xs text-gray-500">Tipo</p>
              <p class="font-medium text-gray-900">
                {{ diet.diet_type.display_name }}
                <span v-if="diet.diet_type.description" class="text-sm text-gray-500">
                  ({{ diet.diet_type.description }})
                </span>
              </p>
            </div>
          </div>
        </div>

        <!-- Nutritional Info Card -->
        <div class="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <h3 class="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Información Nutricional</h3>

          <div class="space-y-3">
            <div v-if="diet.daily_gram_intake">
              <p class="text-xs text-gray-500">Ingesta Diaria</p>
              <p class="font-semibold text-lg text-green-600">{{ diet.daily_gram_intake }}g/día</p>
            </div>

            <div>
              <p class="text-xs text-gray-500">Cantidad Total Semanal</p>
              <p class="font-semibold text-lg text-blue-600">{{ diet.total_quantity }}g</p>
            </div>

            <div v-if="diet.daily_gram_intake" class="pt-2 border-t border-gray-100">
              <div class="flex items-center justify-between text-sm">
                <span class="text-gray-500">Promedio por comida:</span>
                <span class="font-medium">{{ (diet.total_quantity / 7).toFixed(0) }}g</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Descriptions -->
      <div v-if="diet.description || diet.comments" class="space-y-4 mb-6">
        <div v-if="diet.description" class="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
          <p class="text-sm font-semibold text-blue-800 mb-1">📝 Descripción para el Cliente</p>
          <p class="text-gray-700">{{ diet.description }}</p>
        </div>

        <div v-if="diet.comments" class="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
          <p class="text-sm font-semibold text-yellow-800 mb-1">🔒 Comentarios Internos</p>
          <p class="text-gray-700">{{ diet.comments }}</p>
        </div>
      </div>

      <!-- Meals Section -->
      <div class="mt-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-bold text-gray-800 flex items-center">
            <span class="bg-primary-100 text-primary-800 px-3 py-1 rounded-full text-sm mr-2">
              {{ diet.meals ? diet.meals.length : 0 }} Comidas
            </span>
            Plan Semanal
          </h3>
          <button
            v-if="diet.meals && diet.meals.length"
            class="secondary-button text-sm"
            @click.prevent="$emit('printAllMeals')"
          >
            Imprimir Todas
          </button>
        </div>

        <!-- Meals Grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          <div
            v-for="(meal, i) in diet.meals"
            :key="meal.id"
            class="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow"
          >
            <!-- Meal Header -->
            <div class="flex items-center justify-between mb-3 pb-2 border-b border-gray-100">
              <span class="font-semibold text-gray-800">Día {{ i + 1 }}</span>
              <span class="text-sm font-bold text-green-600">
                {{ getMealTotal(meal) }}g
              </span>
            </div>

            <!-- Portions -->
            <div class="space-y-2">
              <div
                v-for="(portion, j) in meal.portions"
                :key="portion.id"
                class="bg-gray-50 rounded p-2 text-sm"
              >
                <div class="flex justify-between items-start">
                  <div class="flex-1">
                    <p class="font-medium text-gray-700">
                      {{ portion.diet_subcategory.diet_category.name }}
                    </p>
                    <p class="text-xs text-gray-500">
                      {{ portion.diet_subcategory.name }}
                    </p>
                  </div>
                  <span class="font-semibold text-primary-600 ml-2">
                    {{ portion.quantity }}g
                  </span>
                </div>
              </div>
            </div>

            <!-- Meal Comments -->
            <div v-if="meal.comments" class="mt-2 pt-2 border-t border-gray-100">
              <p class="text-xs text-gray-500">
                <span class="font-medium">Nota:</span> {{ meal.comments }}
              </p>
            </div>

            <!-- Print button -->
            <button
              class="mt-3 pt-2 border-t border-gray-100 w-full text-xs text-primary-600 hover:text-primary-800 font-medium"
              @click.prevent="$emit('printMeal', meal.id)"
            >
              Imprimir Etiqueta
            </button>
          </div>
        </div>
      </div>

      <!-- Summary Stats -->
      <div class="mt-6 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-4">
        <div class="grid grid-cols-3 gap-4 text-center">
          <div>
            <p class="text-xs text-gray-500 uppercase">Total Porciones</p>
            <p class="text-xl font-bold text-gray-800">{{ getTotalPortions() }}</p>
          </div>
          <div>
            <p class="text-xs text-gray-500 uppercase">Promedio/Comida</p>
            <p class="text-xl font-bold text-gray-800">
              {{ diet.meals ? (diet.total_quantity / diet.meals.length).toFixed(0) : 0 }}g
            </p>
          </div>
          <div>
            <p class="text-xs text-gray-500 uppercase">Categorías Únicas</p>
            <p class="text-xl font-bold text-gray-800">{{ getUniqueCategoriesCount() }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { formatDate, formatDatetime } from "@/utils/dateFormatter";
import { integerToCurrency } from "@/utils/currencyFormatter";

export default {
  name: "Diet Modal",

  props: {
    diet: Object,
  },

  emits: ['close', 'printMeal', 'printAllMeals'],

  methods: {
    formatDate,
    formatDatetime,
    integerToCurrency,

    close() {
      this.$emit("close");
    },

    getMealTotal(meal) {
      if (!meal.portions) return 0;
      return meal.portions.reduce((total, portion) => total + parseFloat(portion.quantity || 0), 0).toFixed(0);
    },

    getTotalPortions() {
      if (!this.diet.meals) return 0;
      let count = 0;
      this.diet.meals.forEach(meal => {
        if (meal.portions) {
          count += meal.portions.length;
        }
      });
      return count;
    },

    getUniqueCategoriesCount() {
      if (!this.diet.meals) return 0;
      const categories = new Set();
      this.diet.meals.forEach(meal => {
        if (meal.portions) {
          meal.portions.forEach(portion => {
            if (portion.diet_subcategory && portion.diet_subcategory.diet_category) {
              categories.add(portion.diet_subcategory.diet_category.name);
            }
          });
        }
      });
      return categories.size;
    }
  },
};
</script>

<style scoped>
/* Custom scrollbar for the modal content */
.max-h-\[90vh\]::-webkit-scrollbar {
  width: 8px;
}

.max-h-\[90vh\]::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.max-h-\[90vh\]::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;
}

.max-h-\[90vh\]::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>